// src/context/AuthContext.tsx

import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { getMe, refreshToken, logoutApi } from '../api/auth';
import { getStorage, setStorage, removeStorage } from '../utils/storage';

export type AuthRole = 'guest' | 'admin' | 'user';

export interface AuthUser {
  id: number;
  username: string;
  role: AuthRole;
  is_active?: boolean;
  created_at?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (token: string, user: AuthUser) => void;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

const getTokenExp = (token: string): number | null => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.exp ?? null;
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(() => getStorage('token'));
  const [loading, setLoading] = useState(() => !!getStorage('token'));
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleRefresh = (currentToken: string) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    const exp = getTokenExp(currentToken);
    if (!exp) return;
    const msUntilRefresh = exp * 1000 - Date.now() - 60_000; // за 60 сек до истечения
    if (msUntilRefresh <= 0) return;
    refreshTimerRef.current = setTimeout(async () => {
      try {
        const newToken = await refreshToken();
        setStorage('token', newToken);
        setToken(newToken);
        scheduleRefresh(newToken);
      } catch {
        // токен истёк — axios-интерсептор сам выкинет на /login
      }
    }, msUntilRefresh);
  };

  // Восстанавливаем пользователя из токена при загрузке страницы
  useEffect(() => {
    const storedToken = getStorage('token');
    if (!storedToken) {
      setLoading(false);
      return;
    }
    getMe()
      .then((userData) => {
        setUser(userData);
        scheduleRefresh(storedToken);
      })
      .catch(() => {
        removeStorage('token');
        setToken(null);
      })
      .finally(() => setLoading(false));

    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []); // eslint-disable-line

  const isAuthenticated = !!token && !!user;

  const login = (newToken: string, newUser: AuthUser) => {
    setStorage('token', newToken);
    setToken(newToken);
    setUser(newUser);
    scheduleRefresh(newToken);
  };

  const logout = () => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    logoutApi().catch(() => {}); // fire-and-forget, не блокируем UI
    removeStorage('token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, isAuthenticated, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
